import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useState } from 'react';
import { Col, Container, Form, Row, Toast } from 'react-bootstrap';
import { Option } from 'react-bootstrap-typeahead/types/types';
import { useNavigate, useParams } from 'react-router-dom';

import { createInvoice, getOrders, getOutlets, getOutletsOwed } from '../../../apis/queries';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import Loader from '../../../components/Loader';
import { Credit, Order } from '../../../types';
import NotFound from '../../NotFound';
import { INVOICES_QUERY_KEY } from '../Invoices';
import GenerateInvoiceButton from './GenerateInvoiceButton';
import OutletsOwedForm, { OutletOption, SelectionGroup } from './OwingOutletsForm';

const CreateInvoice = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const queryClient = useQueryClient();

  const [selectionGroups, setSelectionGroups] = useState<SelectionGroup[]>([]);
  const [modeOfPayment, setModeOfPayment] = useState('');
  const [invoiceAmount, setInvoiceAmount] = useState<number | null>(null);
  const [isInvoiceAmountValid, setIsInvoiceAmountValid] = useState(false);
  const [isCompleteInvoice, setIsCompleteInvoice] = useState(false);
  const [orderBalance, setOrderBalance] = useState<number | null>(null);
  const [orderToInvoice, setOrderToInvoice] = useState<Order | undefined>(undefined);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [comments, setComments] = useState('');
  const navigate = useNavigate();

  const { data: outletsData, isLoading } = useQuery({
    queryKey: ['outlets'],
    queryFn: () => getOutlets({ limit: 10000 })
  });

  const {
    data: outletsOwedData,
    isLoading: outletsOwedLoading,
    refetch: fetchOutletsOwed
  } = useQuery({
    queryKey: ['outletsOwed', orderId],
    queryFn: () => getOutletsOwed(orderId!),
    enabled: false
  });

  const { data: orderData } = useQuery({
    queryKey: ['orderDetails', orderId],
    queryFn: () => getOrders({ _id: orderId }),
    enabled: !!orderId
  });

  useEffect(() => {
    if (orderData) {
      const order = orderData.results.find((order: Order) => order._id.toString() === orderId);
      const balance = order?.balance || 0;
      setOrderToInvoice(order);
      setOrderBalance(balance);
      setIsCompleteInvoice(balance === 0);

      // If not fully invoiced, fetch outlets owed
      if (balance !== 0 && isInvoiceAmountValid) {
        fetchOutletsOwed();
      }
    }
  }, [orderData, orderId, isInvoiceAmountValid]);

  useEffect(() => {
    if (outletsOwedData?.data?.results?.length) {
      const owedOutlets = outletsOwedData.data.results.map((owedOutlet: Credit) => ({
        id: owedOutlet.outlet._id,
        selectedOutlet: {
          id: owedOutlet.outlet._id,
          name: owedOutlet.outlet.name
        },
        amount: owedOutlet.balance,
        prefilled: true
      }));
      setSelectionGroups(owedOutlets);
    } else {
      setSelectionGroups([{ id: Date.now(), selectedOutlet: null, amount: null, prefilled: false }]);
    }
  }, [outletsOwedData]);

  const mutation = useMutation({
    mutationFn: (data: {
      orderId: string;
      amount: string;
      modeOfPayment: string;
      comments?: string;
      creditInfo?: { outletId: string; amount: string }[];
    }) => createInvoice(data.orderId, data.amount, data.modeOfPayment, data.comments, data.creditInfo),
    onSuccess: () => {
      setShowConfirmation(true);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [INVOICES_QUERY_KEY]
      });
    }
  });

  const outlets: Option[] =
    outletsData?.results.map(outlet => ({
      id: outlet._id,
      name: outlet.name
    })) ?? [];

  const handleCreateInvoice = () => {
    const creditInfo = selectionGroups
      .filter(group => group.selectedOutlet?.id && group.amount)
      .map(group => ({
        outletId: group.selectedOutlet?.id || '',
        amount: group.amount?.toString() || '0'
      }));

    const totalOwedAmount = creditInfo.reduce((acc, item) => acc + parseFloat(item.amount), 0);

    if (invoiceAmount !== null && (isCompleteInvoice || totalOwedAmount + invoiceAmount === orderBalance)) {
      const invoiceData = {
        orderId: orderId ?? '',
        amount: invoiceAmount.toString(),
        modeOfPayment: modeOfPayment,
        comments: comments.trim() ? comments : undefined,
        creditInfo: creditInfo.length > 0 ? creditInfo : undefined
      };

      mutation.mutate(invoiceData);
    } else {
      setToastMessage('The amount to be invoiced and owed must equal the order balance.');
      setShowToast(true);
    }
  };

  const handleOutletSelection = (id: number, selected: Option[]) => {
    setSelectionGroups(prev =>
      prev.map(group => (group.id === id ? { ...group, selectedOutlet: selected[0] as OutletOption } : group))
    );
  };

  const handleAmountChange = (id: number, amount: number) => {
    setSelectionGroups(prev => prev.map(group => (group.id === id ? { ...group, amount } : group)));
  };

  const handleAdd = () => {
    setSelectionGroups(prev => [
      ...prev,
      {
        id: Date.now(),
        selectedOutlet: null,
        amount: null,
        prefilled: false
      }
    ]);
  };

  const handleRemove = (id: number) => {
    setSelectionGroups(prev => prev.filter(group => group.id !== id));
  };

  const validateInvoiceAmount = (value: string) => {
    const parsedValue = parseFloat(value);
    const isValidFormat = /^\d+(\.\d{1,2})?$/.test(value) && parsedValue >= 0;

    if (!isValidFormat) {
      setIsInvoiceAmountValid(false);
      setInvoiceAmount(null);
      setIsCompleteInvoice(false);
      return;
    }

    setInvoiceAmount(parsedValue);
    setIsInvoiceAmountValid(true);

    if (orderBalance !== null) {
      if (parsedValue < orderBalance) {
        debouncedFetchOutlets(); // Debounced network call
        setIsCompleteInvoice(false);
      } else if (parsedValue === orderBalance) {
        setIsCompleteInvoice(true);
      } else {
        setIsInvoiceAmountValid(false);
        setIsCompleteInvoice(false);
      }
    } else {
      setIsCompleteInvoice(false);
    }
  };

  // Debounce network call
  const debouncedFetchOutlets = useCallback(debounce(fetchOutletsOwed, 300), []);
  const isFulfilled = orderData?.results[0].status === 'fulfilled';

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center mt-8">
        <Loader />
      </div>
    );
  }

  if (isFulfilled) {
    return <NotFound />;
  }

  const handleCloseConfirmation = () => {
    setLoading(true);

    setTimeout(() => {
      setLoading(false);
      setShowConfirmation(false);
      navigate('/invoices/all');
    }, 2000);
  };

  return (
    <div className="main-content">
      <Container fluid>
        <Row className="justify-content-center">
          <Col xs={12} lg={10} xl={10} className="py-6">
            <Row className="justify-content-center">
              <Col xs={12} md={10} lg={8} xl={6} className="text-center">
                <h1 className="mt-4">Generate Invoice </h1>
                {/* <h3 className="mb-4">Generate invoice for order {orderToInvoice?.orderNumber}</h3> */}
                <div className="mb-4">
                  <div>
                    Order No: <span className="fw-bold">{orderToInvoice?.orderNumber}</span>
                  </div>
                  <div>
                    Customer: <span className="fw-bold">{orderToInvoice?.customer?.name}</span>
                  </div>
                  <div>
                    Outstanding balance: <span className="fw-bold">{orderBalance}</span>
                  </div>
                </div>
              </Col>
            </Row>

            <Row>
              <div className="mt-4">
                <Form.Group>
                  <Form.Label>Payment Mode</Form.Label>
                  <Form.Control
                    as="select"
                    value={modeOfPayment || ''}
                    onChange={e => setModeOfPayment(e.target.value)}
                  >
                    <option value="">Select payment mode...</option>
                    <option value="cash">Cash</option>
                    <option value="transfer">Transfer</option>
                    <option value="debit">Debit</option>
                  </Form.Control>
                  {modeOfPayment === '' && <p className="text-danger mt-2">please select a payment mode</p>}
                </Form.Group>
              </div>
            </Row>
            <Row>
              <div className="mt-4">
                <Form.Group className="my-2">
                  <Form.Label>Invoice Amount</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="100.00"
                    step="0.01"
                    min="0"
                    value={invoiceAmount !== null ? invoiceAmount : ''}
                    onChange={e => validateInvoiceAmount(e.target.value)}
                  />

                  {invoiceAmount === null && (
                    <p className="text-danger mt-2">
                      total amount inputted across all outlets should not be greater than balance
                    </p>
                  )}
                </Form.Group>
              </div>
            </Row>
            <Row>
              <div className="mt-4">
                <Form.Group>
                  <Form.Label>Comments</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Add any comments for this invoice (optional)"
                    value={comments}
                    onChange={e => setComments(e.target.value)}
                  />
                </Form.Group>
              </div>
            </Row>
            {isInvoiceAmountValid && (
              <Row>
                <Col className="mt-6">
                  {outletsOwedLoading ? (
                    <div className="d-flex justify-content-center align-items-center">
                      <Loader />
                    </div>
                  ) : isCompleteInvoice ? (
                    <>
                      <GenerateInvoiceButton
                        onClick={handleCreateInvoice}
                        disabled={!(isInvoiceAmountValid && modeOfPayment)}
                        isLoading={mutation.isPending}
                      />
                    </>
                  ) : (
                    <>
                      <OutletsOwedForm
                        onOutletChanged={handleOutletSelection}
                        outletOptions={outlets || []}
                        onOwedAmountChanged={handleAmountChange}
                        selectionGroups={selectionGroups}
                        handleAddOutlet={handleAdd}
                        handleRemoveOutlet={handleRemove}
                      />
                      <GenerateInvoiceButton
                        onClick={handleCreateInvoice}
                        disabled={!(isInvoiceAmountValid && modeOfPayment)}
                        isLoading={mutation.isPending}
                      />
                    </>
                  )}
                </Col>
              </Row>
            )}

            <ConfirmationModal
              show={showConfirmation}
              onHide={handleCloseConfirmation}
              title="Invoice Confirmation"
              bodyContent={
                <div>
                  <img src="/img/kanban/confirmation.jpg" alt="confirmation" style={{ width: '150px' }} />
                  <p>Invoice has been created successfully!</p>
                </div>
              }
              footerButtonText="Back to Invoice"
              loading={loading}
            />
            <Toast
              onClose={() => setShowToast(false)}
              show={showToast}
              delay={5000}
              autohide
              className="position-fixed top-0 end-0 m-3"
              style={{ backgroundColor: '#dc3545', color: 'white' }}
            >
              <Toast.Body>{toastMessage}</Toast.Body>
            </Toast>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CreateInvoice;
